import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config";
import Layout from "../components/Layout";
import LocationPicker from "../components/LocationPicker";
import PromptListEditor from "../components/PromptListEditor";
import ScheduleEditor from "../components/ScheduleEditor";
import CircleStat from "./geo/CircleStat";
import CostBreakdown from "./geo/CostBreakdown";
import { GhostCards } from "./geo/Pending";
import "./GptSeoTracker.css";

const DEFAULT_SCHEDULE = { intervalDays: 7, dayOfWeek: 1, dayOfMonth: 1 };

function GptSeoTracker() {
  const navigate = useNavigate();
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState({});
  const [clientName, setClientName] = useState("");
  const [clientDomain, setClientDomain] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [runsPerPrompt, setRunsPerPrompt] = useState(5);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [runImmediately, setRunImmediately] = useState(true);
  const [selectedCities, setSelectedCities] = useState([]);
  const [locationKey, setLocationKey] = useState(0);
  const [reports, setReports] = useState([]);
  const [isModelsLoading, setIsModelsLoading] = useState(true);
  const [isReportsLoading, setIsReportsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cost, setCost] = useState({ perRunCost: 0, perMonthCost: 0, breakdown: null });
  const [isPriceLoading, setIsPriceLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsModelsLoading(true);
      try {
        const r = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.dashboard.models}`,
          { credentials: "include" },
        );
        const d = await r.json();
        if (d.success) {
          const visible = (d.data || []).filter((m) => m.showOnGeoDashboard === true);
          setAvailableModels(visible);
          const init = {};
          visible.forEach((m) => (init[m.id] = false));
          setSelectedModels(init);
        }
      } catch (e) {
        setError("Failed to load models.");
      } finally {
        setIsModelsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsReportsLoading(true);
      try {
        const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports`, { credentials: "include" });
        const d = await r.json();
        if (d.success && Array.isArray(d.reports)) setReports(d.reports);
      } catch (e) {
        // noop
      } finally {
        setIsReportsLoading(false);
      }
    };
    load();
  }, []);

  const selectedModelIds = Object.keys(selectedModels).filter((id) => selectedModels[id]);
  const MAX_RESPONSES_PER_RUN = 1200; // mirrors the server cap (one Mongo doc per run)
  const responsesPerRun = prompts.length * selectedModelIds.length * runsPerPrompt;
  // Stable string key so the useCallback dep is a primitive — re-runs only
  // when the actual selection changes, not when the array identity changes.
  const selectedModelsKey = selectedModelIds.join(",");

  const updatePrice = useCallback(async () => {
    if (selectedModelIds.length === 0 || prompts.length === 0 || runsPerPrompt < 1) {
      setCost({ perRunCost: 0, perMonthCost: 0, breakdown: null });
      return;
    }
    setIsPriceLoading(true);
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          models: selectedModelIds,
          numPrompts: prompts.length,
          runsPerPrompt,
          intervalDays: schedule.intervalDays,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setCost({
          perRunCost: d.perRunCost || 0,
          perMonthCost: d.perMonthCost || 0,
          breakdown: d.breakdown || null,
        });
      }
    } catch (e) {
      // noop
    } finally {
      setIsPriceLoading(false);
    }
    // selectedModelIds is intentionally omitted; selectedModelsKey is the
    // stable representation we want to key on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModelsKey, prompts.length, runsPerPrompt, schedule.intervalDays]);

  useEffect(() => { updatePrice(); }, [updatePrice]);

  const groupedModels = availableModels.reduce((acc, m) => {
    (acc[m.provider] ||= []).push(m); return acc;
  }, {});
  const providerNames = { openai: "OpenAI", anthropic: "Anthropic", google: "Google", perplexity: "Perplexity" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");

    if (selectedModelIds.length === 0) return setError("Select at least one model.");
    if (!clientName.trim()) return setError("Client name is required.");
    if (prompts.length === 0) return setError("Add at least one prompt.");
    if (prompts.length > 1000) return setError("Maximum 1000 prompts.");
    if (runsPerPrompt < 1 || runsPerPrompt > 20) return setError("Runs per prompt must be 1–20.");

    setIsSubmitting(true);
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          clientName: clientName.trim(),
          clientDomain: clientDomain.trim(),
          prompts,
          models: selectedModelIds,
          runsPerPrompt,
          cities: selectedCities,
          schedule,
          runImmediately,
        }),
      });
      const d = await r.json();
      if (d.success && d.report) {
        setSuccess("Report created.");
        setReports((prev) => [d.report, ...prev]);
        setClientName(""); setClientDomain(""); setPrompts([]);
        setSelectedModels(Object.fromEntries(Object.keys(selectedModels).map((k) => [k, false])));
        setRunsPerPrompt(5);
        setSchedule(DEFAULT_SCHEDULE);
        setRunImmediately(true);
        setSelectedCities([]);
        setLocationKey((k) => k + 1);
      } else {
        setError(d.error || "Failed to create report.");
      }
    } catch (err) {
      setError("Network error.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="gpt-seo-tracker">
        <h1 className="page-title">GEO Dashboard</h1>
        <div className="seo-tracker-container">
          <div className="tracker-form-section">
            <form onSubmit={handleSubmit} className="tracker-form">
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="form-section">
                <h2>Select Models</h2>
                {isModelsLoading ? (
                  <div className="section-loading"><div className="loading-spinner" /><span>Loading…</span></div>
                ) : Object.keys(groupedModels).length === 0 ? (
                  <p>No GEO-enabled models. Ask an admin to enable them.</p>
                ) : Object.keys(groupedModels).map((provider) => (
                  <div key={provider} className="provider-group">
                    <div className="provider-header">
                      <label className="provider-checkbox">
                        <input type="checkbox"
                          checked={groupedModels[provider].every((m) => selectedModels[m.id])}
                          onChange={() => {
                            const all = groupedModels[provider].every((m) => selectedModels[m.id]);
                            const next = { ...selectedModels };
                            groupedModels[provider].forEach((m) => (next[m.id] = !all));
                            setSelectedModels(next);
                          }} />
                        <span className="provider-name">{providerNames[provider] || provider}</span>
                      </label>
                    </div>
                    <div className="models-list">
                      {groupedModels[provider].map((model) => (
                        <label key={model.id} className="model-checkbox">
                          <input type="checkbox" checked={selectedModels[model.id] || false}
                            onChange={() => setSelectedModels((p) => ({ ...p, [model.id]: !p[model.id] }))} />
                          <span>{model.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-section">
                <h2>Client Name</h2>
                <input className="client-name-input" value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter client name" required />
              </div>

              <div className="form-section">
                <h2>Client Domain <span style={{ fontWeight: 400, fontSize: 13, color: "var(--gpt-seo-text-secondary)" }}>(optional)</span></h2>
                <input className="client-name-input" value={clientDomain}
                  onChange={(e) => setClientDomain(e.target.value)}
                  placeholder="e.g. example.com" />
              </div>

              <div className="form-section">
                <h2>Prompts</h2>
                <PromptListEditor prompts={prompts} onChange={setPrompts} />
              </div>

              <div className="form-section">
                <h2>Runs per prompt</h2>
                <input type="number" min="1" max="20" className="api-calls-input-field"
                  value={runsPerPrompt}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(20, parseInt(e.target.value || "1", 10)));
                    setRunsPerPrompt(n);
                  }} />
                <div className="api-calls-note">
                  Each prompt is sent this many times per model per run.
                  {" "}
                  <span style={{ color: responsesPerRun > MAX_RESPONSES_PER_RUN ? "var(--gpt-seo-text-error-dark)" : "inherit" }}>
                    {responsesPerRun.toLocaleString()} responses per run
                    {responsesPerRun > MAX_RESPONSES_PER_RUN ? ` (max ${MAX_RESPONSES_PER_RUN.toLocaleString()})` : ""}
                  </span>
                </div>
              </div>

              <div className="form-section">
                <h2>Schedule</h2>
                <ScheduleEditor value={schedule} onChange={setSchedule} />
              </div>

              <div className="form-section">
                <LocationPicker
                  key={locationKey}
                  onCitiesChange={setSelectedCities}
                  enabled={!isModelsLoading && !isReportsLoading}
                />
              </div>

              <div className="form-section">
                <label className="model-checkbox" style={{ alignItems: "center" }}>
                  <input type="checkbox" checked={runImmediately}
                    onChange={(e) => setRunImmediately(e.target.checked)} />
                  <span>Run the first report immediately after saving</span>
                </label>
              </div>

              <div className="form-section">
                <h2>Estimated Cost</h2>
                <CostBreakdown cost={cost} loading={isPriceLoading} />
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? "Creating…" : runImmediately ? "Create & Run Now" : "Create Report"}
                </button>
              </div>
            </form>
          </div>

          <div className="reports-section">
            <h2>Reports</h2>
            {isReportsLoading ? (
              <div className="reports-list"><GhostCards count={3} className="report-card" /></div>
            ) : reports.length === 0 ? (
              <div className="no-reports"><p>No reports yet.</p></div>
            ) : (
              <div className="reports-list">
                {reports.map((r) => (
                  <div key={r._id} className="report-card">
                    <div className="report-header">
                      <h3>{r.clientName}</h3>
                      <span className={`report-status status-${r.status}`}>{r.status}</span>
                    </div>
                    <div className="report-metrics-row">
                      {r.lastRun ? (
                        <div className="report-stats-group">
                          <CircleStat label="Mentions" rate={r.lastRun.clientMentionRate || 0} />
                          <CircleStat label="Links" rate={r.lastRun.clientLinkRate || 0} />
                        </div>
                      ) : (
                        <div style={{ color: "var(--gpt-seo-text-secondary)" }}>No runs yet</div>
                      )}
                      <div className="report-meta">
                        <div>
                          <strong>Prompts:</strong> {r.promptCount} · <strong>Models:</strong> {r.modelCount} · <strong>N:</strong> {r.runsPerPrompt}
                        </div>
                        {r.lastRun && (
                          <div style={{ fontSize: 12, color: "var(--gpt-seo-text-secondary)" }}>
                            Run #{r.lastRun.runNumber}
                            {r.lastRun.completedAt ? ` · ${new Date(r.lastRun.completedAt).toLocaleDateString()}` : ""}
                          </div>
                        )}
                        {r.schedule?.nextRunAt && (
                          <div style={{ fontSize: 12, color: "var(--gpt-seo-text-secondary)" }}>
                            Next: {new Date(r.schedule.nextRunAt).toLocaleString()}
                            {r.schedule.paused && " (paused)"}
                          </div>
                        )}
                      </div>
                    </div>
                    <button type="button" className="view-report-button"
                      onClick={() => navigate(`/gpt-seo-tracking/reports/${r._id}`, { state: { clientName: r.clientName } })}>
                      View Report
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default GptSeoTracker;
